import React, { useEffect, useState } from 'react';
import { adminService } from '../services/adminApi';
import { History, User, FileClock, RotateCcw, PenTool, CheckCircle, AlertCircle } from 'lucide-react';

const AuditLogTable = ({ limit = 20 }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const data = await adminService.getAuditLogs({ limit });
        // Ensure data is an array
        setLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch audit logs', err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          setError('Admin access required for audit history. Please sign in with a Super Admin account.');
        } else {
          setError('Failed to load audit history. The database might be unreachable.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [limit]);

  const getActionIcon = (action) => {
    switch (action) {
      case 'REVERT': return <RotateCcw size={14} className="text-orange-600" />;
      case 'UPDATE': return <PenTool size={14} className="text-blue-600" />;
      case 'CREATE': return <CheckCircle size={14} className="text-emerald-600" />;
      default: return <History size={14} className="text-gray-600" />;
    }
  };

  const getActionStyle = (action) => {
    switch (action) {
      case 'REVERT': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'UPDATE': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'CREATE': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (error) {
    return (
      <div className="p-8 text-center bg-red-50 rounded-xl border border-red-100 text-red-600 flex flex-col items-center">
        <AlertCircle size={32} className="mb-2" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-gray-50/50 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
          <tr>
            <th className="px-6 py-4">Action</th>
            <th className="px-6 py-4">User</th>
            <th className="px-6 py-4">Target</th>
            <th className="px-6 py-4">Details</th>
            <th className="px-6 py-4 text-right">Timestamp</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {loading ? (
            [...Array(5)].map((_, i) => (
              <tr key={i} className="animate-pulse">
                <td className="px-6 py-4"><div className="h-4 bg-gray-100 rounded w-16"></div></td>
                <td className="px-6 py-4"><div className="h-4 bg-gray-100 rounded w-24"></div></td>
                <td className="px-6 py-4"><div className="h-4 bg-gray-100 rounded w-32"></div></td>
                <td className="px-6 py-4"><div className="h-4 bg-gray-100 rounded w-48"></div></td>
                <td className="px-6 py-4"><div className="h-4 bg-gray-100 rounded w-24 ml-auto"></div></td>
              </tr>
            ))
          ) : logs.length === 0 ? (
            <tr>
              <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                <div className="flex flex-col items-center gap-2">
                  <FileClock size={32} className="text-gray-300 opacity-50" />
                  <p>No audit records found.</p>
                </div>
              </td>
            </tr>
          ) : (
            logs.map((log, idx) => (
              <tr key={log.id || idx} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-extrabold uppercase border ${getActionStyle(log.action)}`}>
                    {getActionIcon(log.action)}
                    {log.action}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border border-white shadow-sm flex items-center justify-center text-gray-400">
                      <User size={14} />
                    </div>
                    <span className="font-bold text-gray-700 text-xs">{log.admin_email || 'System Automator'}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                   <span className="font-mono text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-md border border-gray-100 shadow-sm">
                      {log.song_id || 'N/A'}
                   </span>
                </td>
                <td className="px-6 py-4">
                  {log.action === 'UPDATE' ? (
                    <div className="text-xs text-gray-600 max-w-xs truncate" title={JSON.stringify(log.new_values)}>
                      <span className="font-bold text-blue-600">Modified:</span> {Object.keys(log.new_values || {}).join(', ') || 'Unknown fields'}
                    </div>
                  ) : log.action === 'REVERT' ? (
                    <span className="text-xs text-orange-600 flex items-center gap-1 font-medium">
                      <RotateCcw size={12} /> Restored original state
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right text-xs text-gray-400 font-medium">
                  {new Date(log.created_at).toLocaleString(undefined, { 
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                  })}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AuditLogTable;